/* ------------------------------------------------------------------ */
/* Seção 5 — Fluxos de Acionamento                                      */
/* ------------------------------------------------------------------ */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/* ------------------------------------------------------------------ */
/* Dados — editar aqui para atualizar o conteúdo                        */
/* ------------------------------------------------------------------ */

interface LinhaParticipacao {
  integracao: string;
  funciona: boolean | null; // null = parcial/condicional
  porque: string;
}

const tabelaParticipacao: LinhaParticipacao[] = [
  {
    integracao: 'DataJud (J02)',
    funciona: true,
    porque: 'Consulta pública por número CNJ — não depende de habilitação',
  },
  {
    integracao: 'DJEN (J01)',
    funciona: false,
    porque: 'Só intima se a publicação citar a OAB dela',
  },
  {
    integracao: 'RPA acervo (eProc/PJe)',
    funciona: false,
    porque: 'Só lista processos onde ela é parte/representante',
  },
  {
    integracao: 'RPA detalhe/peças',
    funciona: false,
    porque: 'Precisa de acesso ao processo (ser parte)',
  },
];

interface PassoFluxo {
  tipo: 'gatilho' | 'acao' | 'decisao' | 'resultado-sim' | 'resultado-nao';
  texto: string;
  filhos?: PassoFluxo[];
}

interface Fluxo {
  id: string;
  titulo: string;
  passos: PassoFluxo[];
}

const fluxos: Fluxo[] = [
  {
    id: 'A',
    titulo: 'Fluxo A — Cliente novo + processo (cadastro manual)',
    passos: [
      {
        tipo: 'acao',
        texto: 'Cliente novo → preenche dados → tem processo ativo?',
        filhos: [
          {
            tipo: 'resultado-sim',
            texto: 'SIM → cadastra número CNJ',
            filhos: [
              {
                tipo: 'gatilho',
                texto: 'DataJud auto-preenche capa + movimentações (sempre, público)',
              },
              {
                tipo: 'decisao',
                texto: 'Advogada é parte/habilitada?',
                filhos: [
                  {
                    tipo: 'resultado-sim',
                    texto: 'SIM → entra no acervo (RPA) + recebe intimações (DJEN)',
                  },
                  {
                    tipo: 'resultado-nao',
                    texto: 'NÃO → só DataJud; sem acervo, sem intimação',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'B',
    titulo: 'Fluxo B — Descoberta automática (sync de acervo)',
    passos: [
      {
        tipo: 'gatilho',
        texto: '"Sincronizar acervo" (manual) ou scheduler (agendado)',
        filhos: [
          {
            tipo: 'acao',
            texto: 'RPA loga no eProc/PJe → lista processos onde ela É parte',
            filhos: [
              {
                tipo: 'decisao',
                texto: 'Cruza por número CNJ:',
                filhos: [
                  {
                    tipo: 'resultado-sim',
                    texto: 'Novo → cria em D02 + DataJud enriquece + casa/cria cliente',
                  },
                  {
                    tipo: 'resultado-nao',
                    texto: 'Existente → enriquece campos vazios + append de andamentos',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'C',
    titulo: 'Fluxo C — Intimações (DJEN, automático)',
    passos: [
      {
        tipo: 'gatilho',
        texto: 'DJEN diário (push)',
        filhos: [
          {
            tipo: 'decisao',
            texto: 'Publicação cita a OAB da advogada?',
            filhos: [
              {
                tipo: 'resultado-sim',
                texto: 'SIM → cria intimação + gera tarefa com prazo + notifica',
                filhos: [
                  {
                    tipo: 'decisao',
                    texto: 'Processo existe no VoxisAdv?',
                    filhos: [
                      {
                        tipo: 'resultado-sim',
                        texto: 'SIM → vincula à intimação',
                      },
                      {
                        tipo: 'resultado-nao',
                        texto: 'NÃO → cria processo pelo número (DataJud enriquece)',
                      },
                    ],
                  },
                ],
              },
              {
                tipo: 'resultado-nao',
                texto: 'NÃO → ignorado',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'D',
    titulo: 'Fluxo D — Atualização periódica',
    passos: [
      {
        tipo: 'gatilho',
        texto: 'Scheduler (1–2×/dia)',
        filhos: [
          {
            tipo: 'acao',
            texto: 'Para cada processo cadastrado:',
            filhos: [
              {
                tipo: 'resultado-sim',
                texto: 'DataJud → busca movimentações novas',
              },
              {
                tipo: 'resultado-sim',
                texto: 'RPA (se no acervo) → verifica andamentos do tribunal',
              },
              {
                tipo: 'acao',
                texto: 'Append de andamentos novos + notifica mudanças relevantes',
              },
            ],
          },
        ],
      },
    ],
  },
];

interface LinhaMatriz {
  gatilho: string;
  datajud: string;
  djen: string;
  rpaAcervo: string;
  rpaDetalhe: string;
}

const matrizAcionamento: LinhaMatriz[] = [
  {
    gatilho: 'Cadastrar nº CNJ',
    datajud: 'auto',
    djen: '—',
    rpaAcervo: '—',
    rpaDetalhe: 'sob demanda',
  },
  {
    gatilho: 'Sync acervo (manual/agendado)',
    datajud: 'enriquece',
    djen: '—',
    rpaAcervo: '✅',
    rpaDetalhe: '—',
  },
  {
    gatilho: 'DJEN diário',
    datajud: 'enriquece novos',
    djen: '✅',
    rpaAcervo: '—',
    rpaDetalhe: '—',
  },
  {
    gatilho: 'Scheduler atualização',
    datajud: '✅',
    djen: '—',
    rpaAcervo: '✅',
    rpaDetalhe: '—',
  },
  {
    gatilho: 'Botão "atualizar processo"',
    datajud: '✅',
    djen: '—',
    rpaAcervo: '—',
    rpaDetalhe: '✅',
  },
];

/* ------------------------------------------------------------------ */
/* Helpers de renderização                                              */
/* ------------------------------------------------------------------ */

function GatilhoBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-violet-500/20 px-2 py-0.5 text-xs font-semibold text-violet-300 border border-violet-500/30">
      <span>⚡</span> GATILHO
    </span>
  );
}

function PassoNode({ passo, depth = 0 }: { passo: PassoFluxo; depth?: number }) {
  const isGatilho = passo.tipo === 'gatilho';
  const isDecisao = passo.tipo === 'decisao';
  const isSim = passo.tipo === 'resultado-sim';
  const isNao = passo.tipo === 'resultado-nao';

  const bgClass = isGatilho
    ? 'bg-violet-500/10 border-violet-500/30 text-violet-200'
    : isDecisao
    ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
    : isSim
    ? 'bg-green-500/10 border-green-500/30 text-green-200'
    : isNao
    ? 'bg-red-500/10 border-red-500/30 text-red-200'
    : 'bg-[var(--surface-muted)]/60 border-[var(--card-border)] text-[var(--fg)]';

  const prefixo = isSim ? '✓ ' : isNao ? '✗ ' : isDecisao ? '◆ ' : '';

  return (
    <div className={depth > 0 ? 'ml-5 mt-2' : 'mt-2'}>
      {depth > 0 && (
        <div className="ml-1 mb-1 flex items-center gap-1">
          <div className="h-4 w-px bg-[var(--card-border)]" />
          <div className="h-px w-3 bg-[var(--card-border)]" />
        </div>
      )}
      <div className={`rounded-lg border px-3 py-2 text-sm ${bgClass}`}>
        {isGatilho && (
          <div className="mb-1">
            <GatilhoBadge />
          </div>
        )}
        <span className="leading-snug">
          {prefixo}{passo.texto}
        </span>
      </div>
      {passo.filhos && passo.filhos.length > 0 && (
        <div>
          {passo.filhos.map((filho, i) => (
            <PassoNode key={i} passo={filho} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function FluxogramaCard({ fluxo }: { fluxo: Fluxo }) {
  return (
    <Card className="border-[var(--card-border)]">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{fluxo.titulo}</CardTitle>
      </CardHeader>
      <CardContent>
        {fluxo.passos.map((passo, i) => (
          <PassoNode key={i} passo={passo} depth={0} />
        ))}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Componente principal                                                 */
/* ------------------------------------------------------------------ */

export function SecaoFluxos() {
  return (
    <section aria-label="Fluxos de acionamento">
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-xs font-bold text-[var(--fg-muted)]">
            SEÇÃO 5
          </span>
          <h2 className="text-xl font-bold tracking-tight">
            Fluxos de Acionamento — quando cada integração roda
          </h2>
        </div>
        <p className="max-w-2xl text-sm text-[var(--fg-muted)] leading-relaxed">
          Mapa de quando e por que cada integração é acionada, partindo dos gatilhos reais (cadastro manual, scheduler, DJEN, botão na tela).
        </p>
      </div>

      {/* Bloco 1 — Conceito: depende de ser parte? */}
      <div className="mb-8">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-violet-400">
          Conceito-chave: "depende de ser parte?"
        </h3>
        <Card className="border-violet-500/20 bg-violet-500/5">
          <CardContent className="pt-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--card-border)]">
                    <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                      Integração
                    </th>
                    <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                      Funciona sem ser parte?
                    </th>
                    <th className="pb-2 text-left text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                      Por quê
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tabelaParticipacao.map((linha, i) => (
                    <tr key={i} className="border-b border-[var(--card-border)]/50 last:border-0">
                      <td className="py-2.5 pr-4 font-mono text-xs font-semibold text-[var(--fg)]">
                        {linha.integracao}
                      </td>
                      <td className="py-2.5 pr-4">
                        {linha.funciona === true ? (
                          <span className="inline-flex items-center gap-1 rounded bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-400">
                            ✅ SIM
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-400">
                            ❌ Não
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 text-xs text-[var(--fg-muted)]">{linha.porque}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 rounded bg-[var(--surface-muted)]/50 px-3 py-2 text-xs text-[var(--fg-muted)] leading-relaxed">
              <span className="font-semibold text-[var(--fg)]">Legenda:</span>{' '}
              Processo onde a advogada <strong>não</strong> é parte (consultoria, due diligence): só o DataJud traz dados, pelo número. Sem acervo, sem intimação automática.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bloco 2 — Fluxogramas */}
      <div className="mb-8">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-violet-400">
          Fluxogramas de acionamento
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {fluxos.map((fluxo) => (
            <FluxogramaCard key={fluxo.id} fluxo={fluxo} />
          ))}
        </div>

        {/* Legenda visual */}
        <div className="mt-4 flex flex-wrap gap-3">
          {[
            { cor: 'bg-violet-500/20 border-violet-500/30 text-violet-300', label: '⚡ GATILHO — dispara a integração' },
            { cor: 'bg-amber-500/10 border-amber-500/30 text-amber-200', label: '◆ Decisão / bifurcação' },
            { cor: 'bg-green-500/10 border-green-500/30 text-green-200', label: '✓ Caminho positivo' },
            { cor: 'bg-red-500/10 border-red-500/30 text-red-200', label: '✗ Caminho negativo' },
          ].map((item, i) => (
            <span
              key={i}
              className={`inline-flex items-center rounded border px-2 py-0.5 text-xs ${item.cor}`}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* Bloco 3 — Matriz de acionamento */}
      <div>
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-violet-400">
          Matriz — quando cada integração aciona
        </h3>
        <Card className="border-[var(--card-border)]">
          <CardContent className="pt-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--card-border)]">
                    {[
                      'Momento / Gatilho',
                      'DataJud',
                      'DJEN',
                      'RPA acervo',
                      'RPA detalhe',
                    ].map((col, i) => (
                      <th
                        key={i}
                        className="pb-2 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)] last:pr-0"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrizAcionamento.map((linha, i) => (
                    <tr key={i} className="border-b border-[var(--card-border)]/50 last:border-0">
                      <td className="py-2.5 pr-4 text-xs font-medium text-[var(--fg)]">
                        {linha.gatilho}
                      </td>
                      {[linha.datajud, linha.djen, linha.rpaAcervo, linha.rpaDetalhe].map(
                        (val, j) => (
                          <td key={j} className="py-2.5 pr-4 text-xs last:pr-0">
                            {val === '—' ? (
                              <span className="text-[var(--fg-muted)]">—</span>
                            ) : val === '✅' ? (
                              <span className="inline-flex items-center rounded bg-green-500/15 px-1.5 py-0.5 text-green-400">
                                ✅
                              </span>
                            ) : (
                              <span className="rounded bg-[var(--surface-muted)]/60 px-1.5 py-0.5 text-[var(--fg-muted)]">
                                {val}
                              </span>
                            )}
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
